import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Star, Sparkles, MessageCircle, Search, Loader2, ShoppingCart, Heart, ChevronRight, ChevronDown, SlidersHorizontal } from 'lucide-react';

// Unsplash source images via picsum - specific seeds for consistent results
const U = (id, w, h) => `https://images.unsplash.com/photo-${id}?w=${w}&h=${h}&fit=crop&crop=center&auto=format&q=80`;

const bgProducts = [
  { id: 1, name: 'Téli Parka', brand: 'The North Face', price: '38 990 Ft', img: U('1539533018447-63fcce2678e3', 180, 220), badge: 'Új', stars: 4.8, reviews: 124 },
  { id: 2, name: 'Air Max Futócipő', brand: 'Nike', price: '42 990 Ft', img: U('1542291026-7eec264c27ff', 180, 220), stars: 4.9, reviews: 341 },
  { id: 3, name: 'Estélyi Midi Ruha', brand: 'Zara', price: '22 990 Ft', img: U('1595777457583-95e059d581b8', 180, 220), badge: '-20%', stars: 4.6, reviews: 87 },
  { id: 4, name: 'Slim Fit Chino', brand: 'H&M', price: '15 990 Ft', img: U('1473966968600-fa801b869a1a', 180, 220), stars: 4.4, reviews: 203 },
  { id: 5, name: 'Chelsea Bakancs', brand: 'Timberland', price: '34 990 Ft', img: U('1542282088-72c9c27ed0cd', 180, 220), badge: 'Hot', stars: 4.7, reviews: 156 },
  { id: 6, name: 'Oversized Hoodie', brand: 'Adidas', price: '18 990 Ft', img: U('1556821840-3a63f15732ce', 180, 220), stars: 4.5, reviews: 98 },
];

const sidebarCategories = [
  { label: 'Összes', count: 248, active: true },
  { label: 'Kabátok', count: 43 },
  { label: 'Cipők', count: 67 },
  { label: 'Ruhák', count: 89 },
  { label: 'Nadrágok', count: 34 },
  { label: 'Kiegészítők', count: 15 },
];

const demoScenarios = [
  {
    query: 'téli kabát, meleg és stílusos',
    filters: [
      { label: 'Kategória', value: 'Kabátok' },
      { label: 'Nem', value: 'Férfi' },
      { label: 'Büdzsé', value: '30 000 – 50 000 Ft' },
    ],
    products: [
      { name: 'Columbia Puffer Jacket', price: '34 500 Ft', match: 94, img: U('1578662996442-48f60103fc96', 72, 72), aiReason: 'Könnyű, meleg, strapabíró – ideális télre.' },
      { name: 'North Face McMurdo', price: '38 990 Ft', match: 87, img: U('1539533018447-63fcce2678e3', 72, 72), aiReason: 'Vízálló, szélálló, -20°C-ig meleg.' },
      { name: 'Patagonia Down Sweater', price: '45 900 Ft', match: 79, img: U('1551488831-00ddcb6c6bd3', 72, 72), aiReason: 'Prémium pehely töltet, kiváló hőszigetelés.' },
    ],
  },
  {
    query: 'futócipő, hosszútávra tervezett',
    filters: [
      { label: 'Kategória', value: 'Futócipők' },
      { label: 'Nem', value: 'Női' },
      { label: 'Büdzsé', value: '35 000 – 55 000 Ft' },
    ],
    products: [
      { name: 'Nike Air Zoom Pegasus', price: '42 990 Ft', match: 95, img: U('1542291026-7eec264c27ff', 72, 72), aiReason: 'Párnázott talp, könnyű – futáshoz ideális.' },
      { name: 'Adidas Ultraboost 23', price: '49 990 Ft', match: 88, img: U('1608231387042-66d1773070a5', 72, 72), aiReason: 'Boost technológia, maximális visszapattanás.' },
      { name: 'New Balance Fresh Foam', price: '35 990 Ft', match: 81, img: U('1491553895911-0055eca6402d', 72, 72), aiReason: 'Puha habszivacs, stabil futótalpszerkezet.' },
    ],
  },
  {
    query: 'elegáns esti ruha, alkalomra',
    filters: [
      { label: 'Kategória', value: 'Ruhák' },
      { label: 'Nem', value: 'Női' },
      { label: 'Büdzsé', value: '20 000 – 35 000 Ft' },
    ],
    products: [
      { name: 'Zara Szatén Midi Ruha', price: '22 990 Ft', match: 96, img: U('1595777457583-95e059d581b8', 72, 72), aiReason: 'Szatén anyag, karcsúsított fazon, bordó szín.' },
      { name: 'H&M Wrap Dress', price: '15 990 Ft', match: 89, img: U('1566479179817-c0a46d7a5070', 72, 72), aiReason: 'Csavaros szabás, sokoldalúan viselhető.' },
      { name: 'Mango Floral Midi', price: '27 990 Ft', match: 83, img: U('1572804013427-4d7ca7268217', 72, 72), aiReason: 'A-vonalú szabás, prémium szövet, virágos.' },
    ],
  },
];

const PHASE_IDLE = 0;
const PHASE_CLICK = 1;
const PHASE_WIDGET_OPEN = 2;
const PHASE_FILTERS = 3;
const PHASE_TYPING = 4;
const PHASE_SEARCHING = 5;
const PHASE_RESULTS = 6;
const PHASE_SCROLLING = 7;
const PHASE_HOLD_END = 8;
const PHASE_CLOSE = 9;

const BASE_TIMINGS = {
  [PHASE_IDLE]: 2400,
  [PHASE_CLICK]: 500,
  [PHASE_WIDGET_OPEN]: 700,
  [PHASE_FILTERS]: 2800,
  [PHASE_SEARCHING]: 1600,
  [PHASE_RESULTS]: 2200,
  [PHASE_SCROLLING]: 3000,
  [PHASE_HOLD_END]: 1800,
  [PHASE_CLOSE]: 700,
};

const StarRow = ({ count }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <svg key={i} width="9" height="9" viewBox="0 0 10 10" fill={i <= Math.floor(count) ? '#facc15' : '#e5e7eb'}>
        <path d="M5 1l1.2 2.5L9 3.8 7 5.8l.5 2.8L5 7.2 2.5 8.6 3 5.8 1 3.8l2.8-.3z" />
      </svg>
    ))}
  </div>
);

const WidgetDemoSection = () => {
  const [scenarioIndex, setScenarioIndex] = useState(0);
  const [phase, setPhase] = useState(PHASE_IDLE);
  const [typedText, setTypedText] = useState('');
  const [typingDone, setTypingDone] = useState(false);
  const [visibleProducts, setVisibleProducts] = useState(0);
  const [filledFilters, setFilledFilters] = useState(0);
  const [cursorPos, setCursorPos] = useState({ x: 20, y: 50 });
  const [cursorClicking, setCursorClicking] = useState(false);
  const scrollRef = useRef(null);
  const scrollRefMobile = useRef(null);

  const scenario = demoScenarios[scenarioIndex];

  const advancePhase = useCallback(() => {
    setPhase((prev) => {
      if (prev === PHASE_CLOSE) {
        setScenarioIndex((si) => (si + 1) % demoScenarios.length);
        setTypedText('');
        setTypingDone(false);
        setVisibleProducts(0);
        setFilledFilters(0);
        return PHASE_IDLE;
      }
      return prev + 1;
    });
  }, []);

  // Timer for all phases except TYPING (handled separately)
  useEffect(() => {
    if (phase === PHASE_TYPING) return;
    const timer = setTimeout(advancePhase, BASE_TIMINGS[phase]);
    return () => clearTimeout(timer);
  }, [phase, advancePhase]);

  // Advance from TYPING only after typing fully completes + 700ms pause
  useEffect(() => {
    if (phase === PHASE_TYPING && typingDone) {
      const timer = setTimeout(advancePhase, 700);
      return () => clearTimeout(timer);
    }
  }, [phase, typingDone, advancePhase]);

  // Cursor movement
  useEffect(() => {
    if (phase === PHASE_IDLE) {
      setCursorPos({ x: 17, y: 50 });
      const t1 = setTimeout(() => setCursorPos({ x: 42, y: 50 }), 800);
      const t2 = setTimeout(() => setCursorPos({ x: 91, y: 88 }), 1400);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    if (phase === PHASE_CLICK) {
      setCursorClicking(true);
      const t = setTimeout(() => setCursorClicking(false), 220);
      return () => clearTimeout(t);
    }
    if (phase === PHASE_WIDGET_OPEN) {
      setCursorPos({ x: 55, y: 30 });
    }
  }, [phase]);

  // Filter fill animation
  useEffect(() => {
    if (phase === PHASE_FILTERS) {
      setFilledFilters(0);
      const timers = scenario.filters.map((_, idx) =>
        setTimeout(() => setFilledFilters(idx + 1), 400 + idx * 620)
      );
      return () => timers.forEach(clearTimeout);
    }
  }, [phase, scenarioIndex, scenario.filters]);

  // Typing effect — sets typingDone when complete
  useEffect(() => {
    if (phase === PHASE_TYPING) {
      const query = scenario.query;
      let i = 0;
      setTypedText('');
      setTypingDone(false);
      const interval = setInterval(() => {
        i++;
        setTypedText(query.slice(0, i));
        if (i >= query.length) {
          clearInterval(interval);
          setTypingDone(true);
        }
      }, 52);
      return () => clearInterval(interval);
    }
  }, [phase, scenario.query]);

  // Products appearing
  useEffect(() => {
    if (phase === PHASE_RESULTS) {
      setVisibleProducts(0);
      [scrollRef, scrollRefMobile].forEach(r => { if (r.current) r.current.scrollTop = 0; });
      const timers = scenario.products.map((_, idx) =>
        setTimeout(() => setVisibleProducts(idx + 1), 200 + idx * 420)
      );
      return () => timers.forEach(clearTimeout);
    }
  }, [phase, scenario.products]);

  // Auto-scroll
  useEffect(() => {
    if (phase === PHASE_SCROLLING) {
      const timers = [scrollRef, scrollRefMobile].map(ref => {
        const container = ref.current;
        if (!container) return null;
        const targetScroll = container.scrollHeight - container.clientHeight;
        if (targetScroll <= 0) return null;
        const duration = 2400;
        const startTime = Date.now();
        const startScroll = container.scrollTop;
        const tick = () => {
          const elapsed = Date.now() - startTime;
          const p = Math.min(elapsed / duration, 1);
          const eased = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
          container.scrollTop = startScroll + targetScroll * eased;
          if (p < 1) requestAnimationFrame(tick);
        };
        return setTimeout(() => requestAnimationFrame(tick), 300);
      });
      return () => timers.forEach(t => t && clearTimeout(t));
    }
  }, [phase]);

  const showWidget = phase >= PHASE_WIDGET_OPEN && phase <= PHASE_HOLD_END;
  const showFilterSection = phase >= PHASE_FILTERS && phase <= PHASE_TYPING;
  const isSearching = phase === PHASE_SEARCHING;
  const isTyping = phase === PHASE_TYPING;
  const showResults = phase >= PHASE_RESULTS && phase <= PHASE_HOLD_END;
  const showChatBubble = phase < PHASE_CLICK;
  const showSearchBar = phase >= PHASE_TYPING;
  const cursorVisible = phase <= PHASE_WIDGET_OPEN;
  const bgDimmed = showWidget;

  return (
    <section id="widget-demo" className="py-24 lg:py-32 bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <span className="text-xs font-bold tracking-[0.2em] text-blue-600 uppercase">Widget Demó</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">Élő demó videó</h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Így működik az AI widget egy valós webshopban — szűrők, keresés, azonnali ajánlások.
          </p>
        </div>

        {/* ── MOBILE PHONE DEMO ── */}
        <div className="md:hidden max-w-[320px] mx-auto">
          <div className="rounded-3xl shadow-2xl border border-gray-200 overflow-hidden bg-white">
            <div className="flex items-center justify-center py-2.5 bg-[#f5f5f7] border-b border-gray-200">
              <div className="w-16 h-1.5 rounded-full bg-gray-300" />
            </div>
            <div className="relative bg-white" style={{ height: 500 }}>
              {/* Mobile webshop bg */}
              <div
                className="absolute inset-0 flex flex-col pointer-events-none transition-all duration-500 bg-white"
                style={{ opacity: bgDimmed ? 0.18 : 1, filter: bgDimmed ? 'blur(1.5px)' : 'none' }}
              >
                {/* Shop navbar */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-white flex-shrink-0">
                  <span className="text-sm font-black text-gray-900 tracking-tighter">STYLE<span className="text-blue-600">HAUS</span></span>
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-full border border-gray-200 flex-1 mx-3">
                    <Search className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    <span className="text-[10px] text-gray-400">Keresés...</span>
                  </div>
                  <div className="relative flex-shrink-0">
                    <ShoppingCart className="w-4 h-4 text-gray-700" />
                    <div className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-[8px] text-white font-bold leading-none">3</span>
                    </div>
                  </div>
                </div>
                {/* Category chips */}
                <div className="flex gap-2 px-4 py-2 border-b border-gray-100 overflow-x-hidden flex-shrink-0">
                  {['Összes', 'Nők', 'Férfiak', 'Sport', 'SALE −30%'].map((cat, i) => (
                    <span key={cat} className={`whitespace-nowrap text-[10px] font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${i === 0 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'}`}>{cat}</span>
                  ))}
                </div>
                {/* Product grid */}
                <div className="flex-1 p-3 overflow-hidden">
                  <p className="text-[10px] text-gray-400 mb-2"><span className="font-semibold text-gray-600">248</span> termék</p>
                  <div className="grid grid-cols-2 gap-2.5">
                    {bgProducts.slice(0, 4).map(p => (
                      <div key={p.id} className="group">
                        <div className="relative rounded-xl overflow-hidden bg-gray-100" style={{ aspectRatio: '3/4' }}>
                          <img src={p.img} alt={p.name} className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }} />
                          {p.badge && (
                            <span className={`absolute top-1.5 left-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded text-white ${p.badge === 'Hot' ? 'bg-orange-500' : p.badge === 'Új' ? 'bg-blue-600' : 'bg-red-500'}`}>{p.badge}</span>
                          )}
                          <button className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white/90 flex items-center justify-center">
                            <Heart className="w-2.5 h-2.5 text-gray-400" />
                          </button>
                        </div>
                        <div className="mt-1.5">
                          <p className="text-[9px] text-gray-400">{p.brand}</p>
                          <p className="text-[11px] font-semibold text-gray-800 leading-tight truncate">{p.name}</p>
                          <p className="text-[11px] font-bold text-gray-900 mt-0.5">{p.price}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Chat bubble */}
              {showChatBubble && (
                <div className="absolute bottom-5 right-5 z-20">
                  <div className="w-[50px] h-[50px] rounded-full bg-blue-600 flex items-center justify-center shadow-xl shadow-blue-600/35">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <div className="absolute inset-0 rounded-full bg-blue-400/25 animate-ping" style={{ animationDuration: '2.5s' }} />
                  <div className="absolute bottom-full right-0 mb-2 whitespace-nowrap bg-gray-900 text-white text-[11px] font-medium px-3 py-1.5 rounded-lg shadow-lg">
                    AI Stylist
                    <div className="absolute top-full right-4 w-0 h-0" style={{ borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #111827' }} />
                  </div>
                </div>
              )}
              {phase === PHASE_CLICK && (
                <div className="absolute bottom-5 right-5 z-20 w-[50px] h-[50px]">
                  <div className="absolute inset-0 rounded-full bg-blue-700 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <div className="absolute inset-0 rounded-full bg-blue-400/50 animate-ping" />
                  <div className="absolute -inset-3 rounded-full bg-blue-300/20 animate-ping" style={{ animationDelay: '0.15s' }} />
                </div>
              )}

              {/* Widget panel — full width on mobile */}
              <div
                className="absolute inset-x-3 top-3 bottom-3 bg-white rounded-2xl border border-gray-200/80 shadow-2xl flex flex-col z-30"
                style={{
                  transform: showWidget ? 'translateX(0) scale(1)' : 'translateX(110%) scale(0.96)',
                  opacity: showWidget ? 1 : 0,
                  transition: 'transform 0.45s cubic-bezier(0.34, 1.4, 0.64, 1), opacity 0.3s ease',
                }}
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-2xl">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white leading-none">AI Stylist</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-300" />
                        <span className="text-[10px] text-blue-100">Online · azonnal válaszol</span>
                      </div>
                    </div>
                  </div>
                  <button className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center text-white/70">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                {phase >= PHASE_WIDGET_OPEN && !showSearchBar && !isSearching && !showResults && (
                  <div className="px-4 pt-3 pb-1 flex-shrink-0">
                    <p className="text-xs text-gray-500 leading-snug">Töltsd ki a szűrőket, majd írd le pontosan mit keresel!</p>
                  </div>
                )}
                {showFilterSection && (
                  <div className="px-3 pt-2 pb-1 flex-shrink-0 space-y-2">
                    {scenario.filters.map((filter, idx) => {
                      const isFilled = idx < filledFilters;
                      return (
                        <div key={filter.label}>
                          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{filter.label}</label>
                          <div className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg border text-xs mt-0.5 transition-all duration-400 ${isFilled ? 'border-blue-200 bg-blue-50 text-blue-700 shadow-sm' : 'border-gray-200 bg-gray-50 text-gray-400'}`}>
                            <span className={isFilled ? 'font-semibold' : ''}>{isFilled ? filter.value : 'Válassz...'}</span>
                            <ChevronDown className={`w-3 h-3 flex-shrink-0 ${isFilled ? 'text-blue-400' : 'text-gray-300'}`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {showSearchBar && (
                  <div className="px-3 pt-2 pb-2 flex-shrink-0 border-t border-gray-100">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Keresés</p>
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${isSearching ? 'border-blue-300 bg-blue-50/60' : 'border-gray-200 bg-gray-50'}`}>
                      {isSearching ? <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin flex-shrink-0" /> : <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                      <span className="text-xs text-gray-700 truncate">
                        {typedText}
                        {isTyping && <span className="animate-blink ml-px text-blue-500">|</span>}
                      </span>
                    </div>
                  </div>
                )}
                {isSearching && (
                  <div className="flex-1 flex flex-col items-center justify-center gap-3 px-4">
                    <div className="relative w-11 h-11">
                      <div className="absolute inset-0 rounded-full border-2 border-blue-100" />
                      <div className="absolute inset-0 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
                      <div className="absolute inset-2 rounded-full bg-blue-50 flex items-center justify-center">
                        <Sparkles className="w-3.5 h-3.5 text-blue-400" />
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-semibold text-gray-800">AI elemzés folyamatban</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">Legjobb egyező termékek keresése...</p>
                    </div>
                  </div>
                )}
                {showResults && (
                  <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 flex-shrink-0">
                    <div className="flex items-center gap-1.5">
                      <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                      <span className="text-xs font-semibold text-gray-800">{scenario.products.length} egyező termék</span>
                    </div>
                    <span className="text-[11px] text-blue-500 font-medium">{visibleProducts}/{scenario.products.length}</span>
                  </div>
                )}
                {showResults && (
                  <div ref={scrollRefMobile} className="flex-1 px-3 py-2.5 space-y-2.5 overflow-hidden" style={{ touchAction: 'none' }} onWheel={(e) => e.preventDefault()}>
                    {scenario.products.map((product, idx) => (
                      <div
                        key={`m-${scenarioIndex}-${idx}`}
                        className={`rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden transition-all duration-500 ${idx < visibleProducts ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                        style={{ transitionDelay: `${idx * 70}ms` }}
                      >
                        <div className="flex gap-3 p-2.5">
                          <div className="w-[62px] h-[62px] rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                            <img src={product.img} alt={product.name} className="w-full h-full object-cover"
                              onError={(e) => { e.target.parentElement.innerHTML = '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#f9fafb;font-size:26px">🛍️</div>'; }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-1">
                              <p className="text-xs font-bold text-gray-900 leading-tight">{product.name}</p>
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${product.match >= 90 ? 'bg-green-100 text-green-700' : product.match >= 80 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{product.match}%</span>
                            </div>
                            <p className="text-sm font-bold text-gray-900 mt-0.5">{product.price}</p>
                            <button className="mt-1.5 w-full text-[10px] font-semibold py-1 rounded-lg bg-blue-600 text-white">Kosárba →</button>
                          </div>
                        </div>
                        <div className="mx-2.5 mb-2.5 px-2.5 py-1.5 bg-amber-50/80 rounded-lg border border-amber-100">
                          <div className="flex items-center gap-1 mb-0.5">
                            <Sparkles className="w-3 h-3 text-amber-500" />
                            <span className="text-[10px] font-bold text-amber-600">Miért ajánljuk</span>
                          </div>
                          <p className="text-[11px] text-gray-600 leading-snug">{product.aiReason}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Browser Mockup */}
        <div className="hidden md:block max-w-5xl mx-auto">
          <div className="rounded-2xl shadow-2xl border border-gray-200 overflow-hidden bg-white">

            {/* Browser chrome */}
            <div className="flex items-center gap-3 px-5 py-3 bg-[#f5f5f7] border-b border-gray-200">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
                <div className="w-3 h-3 rounded-full bg-[#28c840]" />
              </div>
              <div className="flex-1 flex justify-center">
                <div className="flex items-center gap-2 px-4 py-1.5 bg-white rounded-lg border border-gray-300/60 text-xs text-gray-500 w-72 shadow-inner">
                  <svg className="w-3 h-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  styleshop.hu/noi-ferfi-ruhazat
                </div>
              </div>
              <div className="flex gap-2 text-gray-400">
                <ChevronRight className="w-4 h-4 rotate-180" />
                <ChevronRight className="w-4 h-4" />
              </div>
            </div>

            {/* Browser content */}
            <div className="relative overflow-hidden bg-white" style={{ height: 540 }}>

              {/* ── WEBSHOP BACKGROUND ── */}
              <div
                className="absolute inset-0 flex flex-col transition-all duration-500"
                style={{ opacity: bgDimmed ? 0.25 : 1, filter: bgDimmed ? 'blur(1.5px)' : 'none', pointerEvents: 'none' }}
              >
                {/* Shop navbar */}
                <div className="flex-shrink-0 flex items-center justify-between px-5 py-2.5 border-b border-gray-100 bg-white">
                  <div className="flex items-center gap-6">
                    <span className="text-base font-black text-gray-900 tracking-tighter">STYLEHAUS</span>
                    <nav className="flex items-center gap-4 text-[11px] font-medium text-gray-500">
                      <span>Nők</span>
                      <span>Férfiak</span>
                      <span>Sport</span>
                      <span>Márkák</span>
                      <span className="text-red-500 font-bold">SALE −40%</span>
                    </nav>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full border border-gray-200 text-[11px] text-gray-400 w-40">
                      <Search className="w-3 h-3 flex-shrink-0" />
                      <span>Keresés a kínálatban...</span>
                    </div>
                    <Heart className="w-4 h-4 text-gray-400" />
                    <div className="relative">
                      <ShoppingCart className="w-4 h-4 text-gray-600" />
                      <div className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-[8px] text-white font-bold leading-none">3</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content row: sidebar + products */}
                <div className="flex flex-1 overflow-hidden">
                  {/* Sidebar */}
                  <div className="w-[148px] flex-shrink-0 border-r border-gray-100 px-3 py-3 bg-gray-50/40">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Kategória</p>
                    <ul className="space-y-0.5 mb-4">
                      {sidebarCategories.map(cat => (
                        <li key={cat.label} className={`flex items-center justify-between px-2 py-1 rounded-md text-xs cursor-pointer ${cat.active ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-500 hover:bg-gray-100'}`}>
                          <span>{cat.label}</span>
                          <span className="text-[10px] text-gray-400">{cat.count}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Méret</p>
                    <div className="flex flex-wrap gap-1 mb-4">
                      {['XS','S','M','L','XL','XXL'].map(s => (
                        <span key={s} className={`px-1.5 py-0.5 border rounded text-[10px] font-medium cursor-pointer ${s === 'M' ? 'border-blue-400 text-blue-600 bg-blue-50' : 'border-gray-200 text-gray-500'}`}>{s}</span>
                      ))}
                    </div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Ár</p>
                    <div className="space-y-1">
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full w-3/5 bg-blue-500 rounded-full" />
                      </div>
                      <div className="flex justify-between text-[10px] text-gray-400">
                        <span>0 Ft</span><span>80 000 Ft</span>
                      </div>
                    </div>
                  </div>

                  {/* Product area */}
                  <div className="flex-1 overflow-hidden flex flex-col">
                    {/* Sort bar */}
                    <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 flex-shrink-0">
                      <span className="text-xs text-gray-500"><span className="font-semibold text-gray-800">248</span> termék</span>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5 text-[11px] text-gray-500 border border-gray-200 rounded-md px-2 py-1">
                          <SlidersHorizontal className="w-3 h-3" />
                          <span>Szűrés</span>
                        </div>
                        <div className="flex items-center gap-1 text-[11px] text-gray-500 border border-gray-200 rounded-md px-2 py-1">
                          <span>Rendezés: Népszerű</span>
                          <ChevronDown className="w-3 h-3" />
                        </div>
                      </div>
                    </div>

                    {/* Product grid */}
                    <div className="flex-1 px-4 py-3 overflow-hidden">
                      <div className="grid grid-cols-3 gap-3">
                        {bgProducts.map(p => (
                          <div key={p.id} className="group">
                            <div className="relative rounded-xl overflow-hidden bg-gray-100" style={{ aspectRatio: '3/4' }}>
                              <img
                                src={p.img}
                                alt={p.name}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              {p.badge && (
                                <span className={`absolute top-2 left-2 text-[10px] font-bold px-1.5 py-0.5 rounded-md text-white ${p.badge === 'Hot' ? 'bg-orange-500' : p.badge === 'Új' ? 'bg-blue-600' : 'bg-red-500'}`}>
                                  {p.badge}
                                </span>
                              )}
                              <button className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/90 shadow-sm flex items-center justify-center">
                                <Heart className="w-3 h-3 text-gray-400" />
                              </button>
                            </div>
                            <div className="mt-1.5">
                              <p className="text-[10px] text-gray-400">{p.brand}</p>
                              <p className="text-xs font-semibold text-gray-800 truncate">{p.name}</p>
                              <div className="flex items-center gap-1 mt-0.5">
                                <StarRow count={p.stars} />
                                <span className="text-[9px] text-gray-400">({p.reviews})</span>
                              </div>
                              <p className="text-xs font-bold text-gray-900 mt-0.5">{p.price}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── CURSOR ── */}
              <div
                className="absolute z-40 pointer-events-none"
                style={{
                  left: `${cursorPos.x}%`,
                  top: `${cursorPos.y}%`,
                  opacity: cursorVisible ? 1 : 0,
                  transform: `scale(${cursorClicking ? 0.75 : 1})`,
                  transition: 'left 0.7s cubic-bezier(0.4, 0, 0.2, 1), top 0.7s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease, transform 0.12s ease',
                  filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))',
                }}
              >
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <path d="M3.5 1.5L18.5 10.5L11.5 12L8.5 20.5L3.5 1.5Z" fill="white" stroke="#111827" strokeWidth="1.4" strokeLinejoin="round" />
                </svg>
              </div>

              {/* ── CHAT BUBBLE ── */}
              {showChatBubble && (
                <div className="absolute bottom-5 right-5 z-20">
                  <div className="w-[54px] h-[54px] rounded-full bg-blue-600 flex items-center justify-center shadow-xl shadow-blue-600/35">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div className="absolute inset-0 rounded-full bg-blue-400/25 animate-ping" style={{ animationDuration: '2.5s' }} />
                  {/* Tooltip */}
                  <div className="absolute bottom-full right-0 mb-2 whitespace-nowrap bg-gray-900 text-white text-[11px] font-medium px-3 py-1.5 rounded-lg shadow-lg">
                    AI Stylist
                    <div className="absolute top-full right-4 w-0 h-0" style={{ borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #111827' }} />
                  </div>
                </div>
              )}

              {/* Click ripple */}
              {phase === PHASE_CLICK && (
                <div className="absolute bottom-5 right-5 z-20 w-[54px] h-[54px]">
                  <div className="absolute inset-0 rounded-full bg-blue-700 flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div className="absolute inset-0 rounded-full bg-blue-400/50 animate-ping" />
                  <div className="absolute -inset-3 rounded-full bg-blue-300/20 animate-ping" style={{ animationDelay: '0.15s' }} />
                </div>
              )}

              {/* ── WIDGET PANEL ── */}
              <div
                className="absolute top-3 bottom-3 right-3 w-[278px] bg-white rounded-2xl border border-gray-200/80 shadow-2xl flex flex-col z-30"
                style={{
                  transform: showWidget ? 'translateX(0) scale(1)' : 'translateX(115%) scale(0.96)',
                  opacity: showWidget ? 1 : 0,
                  transition: 'transform 0.45s cubic-bezier(0.34, 1.4, 0.64, 1), opacity 0.3s ease',
                }}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-2xl">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white leading-none">AI Stylist</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-300" />
                        <span className="text-[10px] text-blue-100">Online · azonnal válaszol</span>
                      </div>
                    </div>
                  </div>
                  <button className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center text-white/70 hover:bg-white/25">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Welcome / filters */}
                {phase >= PHASE_WIDGET_OPEN && !showSearchBar && !isSearching && !showResults && (
                  <div className="px-4 pt-3 pb-1 flex-shrink-0">
                    <p className="text-xs text-gray-500 leading-snug">
                      Töltsd ki a szűrőket, majd írd le pontosan mit keresel!
                    </p>
                  </div>
                )}

                {/* Filters */}
                {showFilterSection && (
                  <div className="px-3 pt-2 pb-1 flex-shrink-0 space-y-2">
                    {scenario.filters.map((filter, idx) => {
                      const isFilled = idx < filledFilters;
                      return (
                        <div key={filter.label}>
                          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{filter.label}</label>
                          <div
                            className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg border text-xs mt-0.5 transition-all duration-400 ${
                              isFilled
                                ? 'border-blue-200 bg-blue-50 text-blue-700 shadow-sm'
                                : 'border-gray-200 bg-gray-50 text-gray-400'
                            }`}
                          >
                            <span className={isFilled ? 'font-semibold' : ''}>
                              {isFilled ? filter.value : 'Válassz...'}
                            </span>
                            <ChevronDown className={`w-3 h-3 flex-shrink-0 ${isFilled ? 'text-blue-400' : 'text-gray-300'}`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Divider + search bar */}
                {showSearchBar && (
                  <div className="px-3 pt-2 pb-2 flex-shrink-0 border-t border-gray-100">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Keresés</p>
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${isSearching ? 'border-blue-300 bg-blue-50/60' : 'border-gray-200 bg-gray-50'}`}>
                      {isSearching
                        ? <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin flex-shrink-0" />
                        : <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      }
                      <span className="text-xs text-gray-700 truncate">
                        {typedText}
                        {isTyping && <span className="animate-blink ml-px text-blue-500">|</span>}
                      </span>
                    </div>
                  </div>
                )}

                {/* Loading */}
                {isSearching && (
                  <div className="flex-1 flex flex-col items-center justify-center gap-3 px-4">
                    <div className="relative w-11 h-11">
                      <div className="absolute inset-0 rounded-full border-2 border-blue-100" />
                      <div className="absolute inset-0 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
                      <div className="absolute inset-2 rounded-full bg-blue-50 flex items-center justify-center">
                        <Sparkles className="w-3.5 h-3.5 text-blue-400" />
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-semibold text-gray-800">AI elemzés folyamatban</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">Legjobb egyező termékek keresése...</p>
                    </div>
                  </div>
                )}

                {/* Results header */}
                {showResults && (
                  <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 flex-shrink-0">
                    <div className="flex items-center gap-1.5">
                      <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                      <span className="text-xs font-semibold text-gray-800">{scenario.products.length} egyező termék</span>
                    </div>
                    <span className="text-[11px] text-blue-500 font-medium">{visibleProducts}/{scenario.products.length}</span>
                  </div>
                )}

                {/* Results */}
                {showResults && (
                  <div
                    ref={scrollRef}
                    className="flex-1 px-3 py-2.5 space-y-2.5 overflow-hidden"
                    style={{ touchAction: 'none' }}
                    onWheel={(e) => e.preventDefault()}
                  >
                    {scenario.products.map((product, idx) => (
                      <div
                        key={`${scenarioIndex}-${idx}`}
                        className={`rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden transition-all duration-500 ${
                          idx < visibleProducts ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                        }`}
                        style={{ transitionDelay: `${idx * 70}ms` }}
                      >
                        <div className="flex gap-3 p-2.5">
                          <div className="w-[62px] h-[62px] rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                            <img
                              src={product.img}
                              alt={product.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.parentElement.innerHTML = '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#f9fafb;font-size:26px">🛍️</div>';
                              }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-1">
                              <p className="text-xs font-bold text-gray-900 leading-tight">{product.name}</p>
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                                product.match >= 90 ? 'bg-green-100 text-green-700'
                                  : product.match >= 80 ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-500'
                              }`}>{product.match}%</span>
                            </div>
                            <p className="text-sm font-bold text-gray-900 mt-0.5">{product.price}</p>
                            <button className="mt-1.5 w-full text-[10px] font-semibold py-1 rounded-lg bg-blue-600 text-white">
                              Kosárba →
                            </button>
                          </div>
                        </div>
                        <div className="mx-2.5 mb-2.5 px-2.5 py-1.5 bg-amber-50/80 rounded-lg border border-amber-100">
                          <div className="flex items-center gap-1 mb-0.5">
                            <Sparkles className="w-3 h-3 text-amber-500" />
                            <span className="text-[10px] font-bold text-amber-600">Miért ajánljuk</span>
                          </div>
                          <p className="text-[11px] text-gray-600 leading-snug">{product.aiReason}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WidgetDemoSection;
